import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './blocks/ModernBlocks.css';

const BlogDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sample blog data (same as BlogList)
  const blogData = [
    {
      id: 1,
      title: "The Future of Web Development: AI-Powered Tools",
      excerpt: "Discover how artificial intelligence is revolutionizing web development workflows and what it means for developers and businesses.",
      content: `
        <p>Artificial intelligence is transforming every industry, and web development is no exception. From automated code generation to intelligent debugging tools, AI is making development faster, more efficient, and more accessible than ever before.</p>
        
        <h2>The Rise of AI in Web Development</h2>
        <p>In recent years, we've witnessed an explosion of AI-powered tools designed specifically for web developers. These tools are not just novelties—they're becoming essential parts of the modern development workflow.</p>
        
        <h3>Code Generation and Completion</h3>
        <p>AI-powered code generators like GitHub Copilot and Tabnine are revolutionizing how we write code. These tools can:</p>
        <ul>
          <li>Suggest entire functions based on comments</li>
          <li>Auto-complete complex code patterns</li>
          <li>Generate boilerplate code for common tasks</li>
          <li>Provide context-aware suggestions</li>
        </ul>
        
        <h3>Intelligent Debugging</h3>
        <p>AI is also making debugging more efficient. Modern debugging tools can:</p>
        <ul>
          <li>Predict potential bugs before they occur</li>
          <li>Suggest fixes based on common patterns</li>
          <li>Analyze code quality and suggest improvements</li>
          <li>Automatically detect performance issues</li>
        </ul>
        
        <h2>Benefits for Developers</h2>
        <p>The integration of AI in web development brings numerous benefits:</p>
        
        <h3>Increased Productivity</h3>
        <p>By automating repetitive tasks and providing intelligent suggestions, AI tools can significantly increase developer productivity. What used to take hours can now be accomplished in minutes.</p>
        
        <h3>Improved Code Quality</h3>
        <p>AI-powered tools can help maintain consistent coding standards and catch potential issues early in the development process.</p>
        
        <h3>Faster Learning</h3>
        <p>For junior developers, AI tools can serve as intelligent mentors, explaining code patterns and suggesting best practices.</p>
        
        <h2>Challenges and Considerations</h2>
        <p>While AI tools offer tremendous benefits, there are important considerations:</p>
        
        <h3>Over-reliance</h3>
        <p>Developers must be careful not to become overly dependent on AI tools. Understanding the underlying principles is still crucial.</p>
        
        <h3>Code Quality</h3>
        <p>AI-generated code isn't always perfect. Developers need to review and understand the code they're using.</p>
        
        <h3>Privacy and Security</h3>
        <p>Using AI tools often means sharing code with third-party services, which raises privacy and security concerns.</p>
        
        <h2>The Future Outlook</h2>
        <p>As AI technology continues to evolve, we can expect even more sophisticated tools. The future might include:</p>
        <ul>
          <li>AI-powered project management and estimation</li>
          <li>Automated testing and quality assurance</li>
          <li>Intelligent deployment and monitoring</li>
          <li>Personalized learning paths for developers</li>
        </ul>
        
        <h2>Conclusion</h2>
        <p>AI-powered tools are not just a trend—they're the future of web development. By embracing these tools while maintaining a solid understanding of fundamental principles, developers can work more efficiently and create better applications.</p>
        
        <p>The key is to use AI as a powerful assistant rather than a replacement for human creativity and problem-solving skills. The most successful developers will be those who can effectively leverage AI tools while maintaining their unique perspective and expertise.</p>
      `,
      author: "Sarah Johnson",
      authorAvatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      authorBio: "Sarah is a senior software engineer with over 8 years of experience in web development. She specializes in AI integration and modern web technologies.",
      date: "2024-01-15",
      category: "Technology",
      tags: ["AI", "Web Development", "Future Tech"],
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=400&fit=crop",
      featured: true
    },
    {
      id: 2,
      title: "Building Scalable Microservices Architecture",
      excerpt: "Learn the best practices for designing and implementing microservices that can scale with your business growth.",
      content: `
        <p>Microservices architecture has become the go-to solution for building scalable, maintainable applications. But implementing it correctly requires careful planning and understanding of key principles.</p>
        
        <h2>What are Microservices?</h2>
        <p>Microservices are an architectural style where an application is built as a collection of small, independent services that communicate through well-defined APIs.</p>
        
        <h2>Key Benefits</h2>
        <ul>
          <li>Independent deployment and scaling</li>
          <li>Technology diversity</li>
          <li>Fault isolation</li>
          <li>Team autonomy</li>
        </ul>
      `,
      author: "Michael Chen",
      authorAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      authorBio: "Michael is a solutions architect with expertise in distributed systems and cloud-native applications.",
      date: "2024-01-12",
      category: "Architecture",
      tags: ["Microservices", "Scalability", "Backend"],
      readTime: "8 min read",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop"
    },
    {
      id: 3,
      title: "React Performance Optimization Techniques",
      excerpt: "Master the art of optimizing React applications for better performance and user experience.",
      content: `
        <p>React is powerful, but without proper optimization, your applications can become slow and unresponsive. Here are proven techniques to boost your React app's performance.</p>
        
        <h2>Key Optimization Strategies</h2>
        <p>From code splitting to memoization, learn the techniques that will make your React apps lightning fast.</p>
      `,
      author: "Emily Rodriguez",
      authorAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      authorBio: "Emily is a frontend specialist with deep expertise in React and modern JavaScript frameworks.",
      date: "2024-01-10",
      category: "Frontend",
      tags: ["React", "Performance", "JavaScript"],
      readTime: "6 min read",
      image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&h=400&fit=crop"
    }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      const foundPost = blogData.find(p => p.id === parseInt(id));
      setPost(foundPost);
      
      // Get related posts (same category, different posts)
      const related = blogData
        .filter(p => p.category === foundPost?.category && p.id !== foundPost?.id)
        .slice(0, 3);
      setRelatedPosts(related);
      
      setLoading(false);
    }, 1000);
  }, [id]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const sharePost = (platform) => {
    const url = window.location.href;
    const title = post?.title;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    };
    
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="blog-loading">
        <div className="loading-spinner"></div>
        <p>Loading article...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-not-found">
        <h1>Article Not Found</h1>
        <p>The article you're looking for doesn't exist.</p>
        <Link to="/blog" className="back-to-blog">Back to Blog</Link>
      </div>
    );
  }

  return (
    <div className="blog-detail-page">
      {/* Hero Section */}
      <div className="blog-detail-hero">
        <div className="blog-detail-hero-container">
          <div className="blog-detail-image">
            <img src={post.image} alt={post.title} />
          </div>
          
          <div className="blog-detail-hero-content">
            <div className="blog-detail-meta">
              <span className="blog-detail-category">{post.category}</span>
              <span className="blog-detail-date">{formatDate(post.date)}</span>
              <span className="blog-detail-read-time">{post.readTime}</span>
            </div>
            
            <h1 className="blog-detail-title">{post.title}</h1>
            <p className="blog-detail-excerpt">{post.excerpt}</p>
            
            <div className="blog-detail-author">
              <img src={post.authorAvatar} alt={post.author} className="blog-detail-author-avatar" />
              <div className="blog-detail-author-info">
                <span className="blog-detail-author-name">{post.author}</span>
                <span className="blog-detail-author-bio">{post.authorBio}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="blog-detail-main">
        <div className="blog-detail-container">
          <div className="blog-detail-content">
            <article className="blog-article">
              <div 
                className="blog-article-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </article>
            
            {/* Tags */}
            <div className="blog-detail-tags">
              <h3>Tags:</h3>
              <div className="blog-detail-tag-list">
                {post.tags.map(tag => (
                  <span key={tag} className="blog-detail-tag">{tag}</span>
                ))}
              </div>
            </div>
            
            {/* Social Sharing */}
            <div className="blog-detail-sharing">
              <h3>Share this article:</h3>
              <div className="blog-detail-share-buttons">
                <button 
                  className="share-button twitter"
                  onClick={() => sharePost('twitter')}
                >
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                  Twitter
                </button>
                
                <button 
                  className="share-button linkedin"
                  onClick={() => sharePost('linkedin')}
                >
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </button>
                
                <button 
                  className="share-button facebook"
                  onClick={() => sharePost('facebook')}
                >
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </button>
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <aside className="blog-detail-sidebar">
            <div className="blog-detail-author-card">
              <img src={post.authorAvatar} alt={post.author} className="sidebar-author-avatar" />
              <h3 className="sidebar-author-name">{post.author}</h3>
              <p className="sidebar-author-bio">{post.authorBio}</p>
            </div>
            
            {relatedPosts.length > 0 && (
              <div className="blog-detail-related">
                <h3>Related Articles</h3>
                <div className="blog-detail-related-posts">
                  {relatedPosts.map(relatedPost => (
                    <article key={relatedPost.id} className="blog-detail-related-post">
                      <img src={relatedPost.image} alt={relatedPost.title} />
                      <div className="blog-detail-related-content">
                        <h4>
                          <Link to={`/blog/${relatedPost.id}`}>{relatedPost.title}</Link>
                        </h4>
                        <span className="blog-detail-related-date">{formatDate(relatedPost.date)}</span>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail; 